/**
 * Account & Profile Articles
 */

import type { HelpArticle } from "../types";

export const ACCOUNT_ARTICLES: HelpArticle[] = [
  {
    id: "acc-001",
    slug: "managing-your-profile",
    title: "Managing Your Profile",
    description: "Update your personal information and preferences",
    category: "account",
    order: 1,
    keywords: ["profile", "settings", "personal", "information", "update"],
    content: `
# Managing Your Profile

Keep your profile information up to date for smooth transactions and compliance.

## Accessing Profile Settings

1. Click your avatar in the sidebar
2. Select "Settings"
3. Navigate to "Profile" tab

## Editable Information

**Personal Details**
- Display name (how your name appears)
- Phone number (optional)
- Preferred language

**Address**
- Street address
- City
- State/Province
- Postal code
- Country

## Information You Cannot Change

Some information is locked after verification:
- Legal name (contact support to change)
- Date of birth
- Nationality
- ID document details

## Updating Your Address

If you've moved:
1. Go to Settings > Profile
2. Click "Update Address"
3. Enter new address details
4. You may need to reverify if moving countries

## Profile Photo

- Optional but recommended
- Helps identify your account
- Must be appropriate (no logos, etc.)

## Communication Preferences

Control what notifications you receive:
- Transaction updates
- Security alerts
- Marketing communications
- Product announcements

## Data Export

You can download your data:
1. Go to Settings > Privacy
2. Click "Export Data"
3. Receive download link via email
4. Available in JSON format
    `.trim(),
  },
  {
    id: "acc-002",
    slug: "changing-email-phone",
    title: "Changing Email or Phone",
    description: "How to update your contact information",
    category: "account",
    order: 2,
    keywords: ["email", "phone", "change", "update", "contact"],
    content: `
# Changing Email or Phone

Your email is your primary login method. Here's how to update it safely.

## Changing Your Email

### Requirements
- Access to current email
- Access to new email
- 2FA if enabled

### Steps
1. Go to Settings > Security
2. Click "Change Email"
3. Enter new email address
4. Verify ownership of current email
5. Verify ownership of new email
6. Confirmation email sent to both

### Important Notes
- Both emails must be verified
- Process takes a few minutes
- Old email can't be reused immediately

## Adding Phone Number

Phone is optional but adds security:
1. Go to Settings > Profile
2. Click "Add Phone"
3. Enter number with country code
4. Verify via SMS code

## Changing Phone Number

1. Go to Settings > Profile
2. Click "Change Phone"
3. Verify current number
4. Enter and verify new number

## Lost Access to Email?

If you can't access your email:
1. Contact support immediately
2. Provide identity verification
3. Answer security questions
4. Recovery takes 24-48 hours

## Security Considerations

- Email changes trigger security alerts
- Recent login history reviewed
- Large withdrawals may be paused
- Additional verification may be required
    `.trim(),
  },
  {
    id: "acc-003",
    slug: "account-verification-tiers",
    title: "Account Verification Tiers",
    description: "Understanding different verification levels",
    category: "account",
    order: 3,
    keywords: ["verification", "tier", "level", "limits", "upgrade"],
    content: `
# Account Verification Tiers

Your verification level determines your transaction limits and available features.

## Verification Levels

### Tier 1: Basic
**Requirements:**
- Email verification only

**Limits:**
- View-only access
- No transactions

### Tier 2: Standard
**Requirements:**
- Government-issued ID
- Selfie verification

**Limits:**
- $50,000 monthly volume
- $10,000 per transaction
- All standard features

### Tier 3: Enhanced
**Requirements:**
- Standard + Proof of address
- Additional documentation

**Limits:**
- $500,000 monthly volume
- $100,000 per transaction
- Priority support

### Tier 4: Enterprise
**Requirements:**
- Business verification
- Dedicated account manager

**Limits:**
- Custom limits
- API access
- White-glove support

## How to Upgrade

### Standard to Enhanced
1. Go to Settings > Verification
2. Click "Upgrade to Enhanced"
3. Upload proof of address:
   - Utility bill (< 3 months)
   - Bank statement (< 3 months)
   - Tax document
4. Wait for review (24-48 hours)

### Enhanced to Enterprise
Contact our sales team at enterprise@supabank.xyz

## Checking Your Tier

View your current tier:
1. Go to Settings > Verification
2. See "Current Level" badge
3. View remaining limits

## Tier Benefits Comparison

| Feature | Basic | Standard | Enhanced | Enterprise |
|---------|-------|----------|----------|------------|
| Deposits | | | | |
| Withdrawals | | | | |
| API Access | | | | |
| Priority Support | | | | |
| Dedicated Manager | | | | |
    `.trim(),
  },
  {
    id: "acc-004",
    slug: "closing-your-account",
    title: "Closing Your Account",
    description: "How to close your Supabank account",
    category: "account",
    order: 4,
    keywords: ["close", "delete", "account", "terminate", "cancel"],
    content: `
# Closing Your Account

If you need to close your account, here's what you need to know.

## Before You Close

Please ensure:
- All pending transactions are complete
- Funds have been withdrawn
- No outstanding issues or disputes
- You've downloaded any needed records

## Closure Process

1. Go to Settings > Account
2. Scroll to "Close Account"
3. Click "Request Account Closure"
4. Confirm your identity
5. Select closure reason
6. Confirm closure

## What Happens Next

**Immediately:**
- Account access revoked
- No new transactions possible

**Within 30 days:**
- Data retention review
- Remaining balances processed
- Final statements generated

**Permanent:**
- Account permanently deleted
- Cannot be recovered
- Email cannot be reused for 90 days

## Data Retention

We retain some data as required by law:
- Transaction records (7 years)
- Identity documents (5 years)
- Compliance records (varies)

## Getting Your Funds

Before closing:
1. Check all balances
2. Initiate withdrawal to bank
3. Wait for completion
4. Then request closure

## Reactivation

Once closed, accounts cannot be reactivated. You would need to create a new account and complete verification again.

## Questions?

If you're closing due to an issue, consider contacting support first. We may be able to help resolve your concern.
    `.trim(),
  },
  {
    id: "acc-005",
    slug: "account-recovery",
    title: "Account Recovery",
    description: "Regain access to your account",
    category: "account",
    order: 5,
    keywords: ["recovery", "access", "locked", "help", "support"],
    content: `
# Account Recovery

Lost access to your account? Here's how to recover it.

## Common Access Issues

### Can't Receive Magic Link
- Check spam/junk folder
- Verify email address spelling
- Try a different email client
- Wait a few minutes and retry

### Magic Link Expired
- Links expire after 15 minutes
- Request a new link
- Check for multiple emails

### Account Locked
- Too many failed attempts
- Wait 30 minutes
- Or contact support

## Recovery Steps

### With Email Access

1. Go to login page
2. Enter your email
3. Click "Send Magic Link"
4. Check email (and spam)
5. Click link within 15 minutes

### Without Email Access

1. Contact support@supabank.xyz
2. Provide:
   - Account email
   - Full legal name
   - Date of birth
   - Last transaction details
3. Complete identity verification
4. Support will assist with recovery

## Identity Verification for Recovery

You may need to provide:
- Photo of ID document
- Selfie with handwritten note
- Proof of address
- Account-specific information

## Timeline

| Recovery Type | Typical Time |
|---------------|--------------|
| Standard login | Immediate |
| Password/2FA reset | 1-2 hours |
| Full account recovery | 24-48 hours |

## Prevention Tips

- Keep your email secure
- Enable 2FA
- Save backup codes
- Update contact info regularly
- Use a password manager for email
    `.trim(),
  },
  {
    id: "acc-006",
    slug: "business-accounts",
    title: "Business Accounts",
    description: "Information about business account features",
    category: "account",
    order: 6,
    keywords: ["business", "corporate", "company", "enterprise", "organization"],
    content: `
# Business Accounts

Supabank offers business accounts for companies needing to convert crypto to fiat at scale.

## Business Account Benefits

- Higher transaction limits
- Multiple user access
- API integration
- Dedicated support
- Custom reporting
- Invoicing features

## Eligibility

Business accounts are available for:
- Registered corporations
- LLCs and partnerships
- Sole proprietorships (with business license)
- Non-profit organizations

## Required Documentation

### Company Documents
- Certificate of incorporation
- Articles of association
- Business license (if applicable)
- Tax registration certificate

### Ownership Verification
- Beneficial owner information
- Shareholder register
- Director identification

### Address Verification
- Business utility bill
- Bank statement
- Lease agreement

## Application Process

1. **Sign up** as individual first
2. **Complete personal KYC**
3. **Request business upgrade** in Settings
4. **Submit business documents**
5. **Verification review** (3-5 business days)
6. **Account activated**

## Multi-User Access

Business accounts can have:
- Multiple admins
- View-only users
- Approval workflows
- Activity audit logs

## Business Limits

| Tier | Monthly Volume | Per Transaction |
|------|----------------|-----------------|
| Business Standard | $1,000,000 | $250,000 |
| Business Pro | $5,000,000 | $500,000 |
| Enterprise | Custom | Custom |

## API Access

Business accounts include:
- REST API access
- Webhook notifications
- Sandbox environment
- Technical documentation
- Integration support

## Get Started

Contact enterprise@supabank.xyz or apply through your account settings.
    `.trim(),
  },
];
